import React, { useMemo, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Card, CardContent, Chip, Container, Divider, FormControl, IconButton, InputAdornment, Menu, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useTheme } from '@mui/material';
import { Icon } from '@iconify/react';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import EntityViewDrawer, { EntitySection } from '../components/ui/EntityViewDrawer';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { useToast } from '../components/ui/ToastProvider';
import './paysysShowcase.css';
type PartnerScope = 'global' | string;
type RuleKind = 'nationality' | 'purpose';
type PurposeType = 'Word' | 'Purpose Code';
type MatchType = 'Contains' | 'Exact Match';
type NationalityOption = {
  name: string;
  code: string;
  iso8583: string;
  flag: string;
};
type NationalityRule = {
  id: string;
  partnerScope: PartnerScope;
  nationality: string;
  nationalityCode: string;
  iso8583Code: string;
  flag: string;
  effectiveDate: string;
  endDate: string;
};
type PurposeRule = {
  id: string;
  partnerScope: PartnerScope;
  value: string;
  type: PurposeType;
  matchType: MatchType;
  effectiveDate: string;
  endDate: string;
};
type BlockRulesPageProps = {
  kind: RuleKind;
  onLogout: () => void;
};
const drawerWidth = 240;
const nationalityOptions: NationalityOption[] = [{
  name: 'India',
  code: '356',
  iso8583: 'IND',
  flag: 'IN'
}, {
  name: 'UAE',
  code: '784',
  iso8583: 'ARE',
  flag: 'AE'
}, {
  name: 'Saudi Arabia',
  code: '682',
  iso8583: 'SAU',
  flag: 'SA'
}, {
  name: 'UK',
  code: '826',
  iso8583: 'GBR',
  flag: 'GB'
}, {
  name: 'US',
  code: '840',
  iso8583: 'USA',
  flag: 'US'
}, {
  name: 'Kuwait',
  code: '414',
  iso8583: 'KWT',
  flag: 'KW'
}, {
  name: 'Qatar',
  code: '634',
  iso8583: 'QAT',
  flag: 'QA'
}, {
  name: 'Bahrain',
  code: '048',
  iso8583: 'BHR',
  flag: 'BH'
}, {
  name: 'Oman',
  code: '512',
  iso8583: 'OMN',
  flag: 'OM'
}];
const initialNationalityRules: NationalityRule[] = [{
  id: 'NAT-1001',
  partnerScope: 'global',
  nationality: 'India',
  nationalityCode: '356',
  iso8583Code: 'IND',
  flag: 'IN',
  effectiveDate: '2026-06-18',
  endDate: ''
}];
const initialPurposeRules: PurposeRule[] = [{
  id: 'POP-1001',
  partnerScope: 'global',
  value: 'crypto',
  type: 'Word',
  matchType: 'Contains',
  effectiveDate: '2026-06-18',
  endDate: ''
}, {
  id: 'POP-1002',
  partnerScope: 'global',
  value: '6501',
  type: 'Purpose Code',
  matchType: 'Exact Match',
  effectiveDate: '2026-06-18',
  endDate: ''
}];
const pageCopy = {
  nationality: {
    title: 'Nationality Block',
    activePage: 'nationality-block',
    route: '/nationality-block',
    icon: 'lucide:flag',
    purpose: 'Blocks transactions when the sender/remitter nationality matches a configured rule.',
    tableTitle: 'Nationality blocks',
    exportPrefix: 'nationality-block',
    emptyState: 'No nationality block rules found'
  },
  purpose: {
    title: 'Purpose of Payment Block',
    activePage: 'purpose-payment-block',
    route: '/purpose-payment-block',
    icon: 'lucide:file-warning',
    purpose: 'Blocks transactions when the purpose of payment contains a configured word or matches a configured purpose code.',
    tableTitle: 'Purpose of payment blocks',
    exportPrefix: 'purpose-payment-block',
    emptyState: 'No purpose of payment block rules found'
  }
};
const scopeLabel = (scope: PartnerScope) => scope === 'global' ? 'Global' : scope;
const normalizeScope = (partner: string | null) => !partner || partner === 'Global' ? 'global' : partner;
const todayStamp = () => new Date().toISOString().slice(0, 10);
const downloadCsv = (fileName: string, headers: string[], rows: Array<Array<string>>) => {
  const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;
  const csv = [headers, ...rows].map(row => row.map(escape).join(',')).join('\n');
  const blob = new Blob([csv], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5
  }
};
const FilterTextField = ({
  value,
  placeholder,
  onChange,
  type = 'text'
}: {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  type?: string;
}) => <TextField size="small" type={type} value={value} placeholder={placeholder} onChange={event => onChange(event.target.value)} sx={{
  width: '100%',
  ...fieldSx
}} InputProps={{
  startAdornment: type === 'date' ? undefined : <InputAdornment position="start" data-id="element-2967" data-mp="33afthlnj174c">
          <Icon icon="lucide:search" width={14} height={14} data-id="element-2968" data-mp="3371t2iyfb7zj" />
        </InputAdornment>,
  sx: {
    height: 36,
    fontSize: '0.8rem'
  }
}} data-id="element-2966" data-mp="2pbpziew07nk7" />;
const useShellStyles = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return {
    pageBg: isDark ? '#071622' : '#F5F8FB',
    surface: isDark ? '#102536' : '#FFFFFF',
    surfaceSoft: isDark ? '#17344A' : '#EDF4F8',
    textStrong: isDark ? '#F7FBFF' : '#071827',
    muted: isDark ? '#9AB0C0' : '#617386',
    cardBorder: isDark ? '#254459' : '#D9E4EC',
    cardShadow: isDark ? '0 12px 30px rgba(0,0,0,0.22)' : '0 12px 30px rgba(15,23,42,0.08)'
  };
};
const BlockRulesPage: React.FC<BlockRulesPageProps> = ({
  kind,
  onLogout
}) => {
  const copy = pageCopy[kind];
  const [mobileOpen, setMobileOpen] = useState(false);
  const [nationalityRules, setNationalityRules] = useState<NationalityRule[]>(initialNationalityRules);
  const [purposeRules, setPurposeRules] = useState<PurposeRule[]>(initialPurposeRules);
  const [selectedPartner, setSelectedPartner] = useState<string | null>('Global');
  const [selectedNationality, setSelectedNationality] = useState<NationalityOption | null>(nationalityOptions[0]);
  const [purposeValue, setPurposeValue] = useState('');
  const [purposeType, setPurposeType] = useState<PurposeType>('Word');
  const [matchType, setMatchType] = useState<MatchType>('Contains');
  const [effectiveDate, setEffectiveDate] = useState(todayStamp());
  const [endDate, setEndDate] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formError, setFormError] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [viewRow, setViewRow] = useState<NationalityRule | PurposeRule | null>(null);
  const [deleteRow, setDeleteRow] = useState<NationalityRule | PurposeRule | null>(null);
  const [actionAnchor, setActionAnchor] = useState<HTMLElement | null>(null);
  const [actionRow, setActionRow] = useState<NationalityRule | PurposeRule | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const {
    showToast
  } = useToast();
  const styles = useShellStyles();
  const setFilter = (key: string, value: string) => {
    setFilters(current => ({
      ...current,
      [key]: value
    }));
  };
  const resetForm = () => {
    setSelectedPartner('Global');
    setSelectedNationality(nationalityOptions[0]);
    setPurposeValue('');
    setPurposeType('Word');
    setMatchType('Contains');
    setEffectiveDate(todayStamp());
    setEndDate('');
    setEditingId(null);
    setFormError('');
  };
  const handlePurposeTypeChange = (nextType: PurposeType) => {
    setPurposeType(nextType);
    setMatchType(nextType === 'Word' ? 'Contains' : 'Exact Match');
  };
  const filteredNationalityRules = useMemo(() => {
    return nationalityRules.filter(rule => {
      const anyQuery = filters.any?.toLowerCase() || '';
      const matchesAny = !anyQuery || [rule.nationality, rule.nationalityCode, rule.iso8583Code].some(value => value.toLowerCase().includes(anyQuery));
      const matchesName = !filters.nationality || rule.nationality.toLowerCase().includes(filters.nationality.toLowerCase());
      const matchesCode = !filters.nationalityCode || rule.nationalityCode.includes(filters.nationalityCode);
      const matchesIso = !filters.iso8583Code || rule.iso8583Code.toLowerCase().includes(filters.iso8583Code.toLowerCase());
      const matchesEffective = !filters.effectiveDate || rule.effectiveDate === filters.effectiveDate;
      const matchesEnd = !filters.endDate || rule.endDate === filters.endDate;
      return matchesAny && matchesName && matchesCode && matchesIso && matchesEffective && matchesEnd;
    });
  }, [filters, nationalityRules]);
  const filteredPurposeRules = useMemo(() => {
    return purposeRules.filter(rule => {
      const matchesSearch = !filters.search || rule.value.toLowerCase().includes(filters.search.toLowerCase());
      const matchesType = !filters.type || filters.type === 'all' || rule.type === filters.type;
      const matchesMatchType = !filters.matchType || filters.matchType === 'all' || rule.matchType === filters.matchType;
      const matchesEffective = !filters.effectiveDate || rule.effectiveDate === filters.effectiveDate;
      const matchesEnd = !filters.endDate || rule.endDate === filters.endDate;
      return matchesSearch && matchesType && matchesMatchType && matchesEffective && matchesEnd;
    });
  }, [filters, purposeRules]);
  const visibleNationalityRules = filteredNationalityRules;
  const visiblePurposeRules = filteredPurposeRules;
  const handleAddOrSave = () => {
    setFormError('');
    if (!effectiveDate) {
      setFormError('Effective Date is required.');
      return;
    }
    if (endDate && endDate < effectiveDate) {
      setFormError('End Date cannot be earlier than Effective Date.');
      return;
    }
    if (kind === 'nationality') {
      if (!selectedNationality) {
        setFormError('Nationality is required.');
        return;
      }
      const newRule: NationalityRule = {
        id: editingId || `NAT-${Date.now()}`,
        partnerScope: normalizeScope(selectedPartner),
        nationality: selectedNationality.name,
        nationalityCode: selectedNationality.code,
        iso8583Code: selectedNationality.iso8583,
        flag: selectedNationality.flag,
        effectiveDate,
        endDate
      };
      setNationalityRules(rules => editingId ? rules.map(rule => rule.id === editingId ? newRule : rule) : [newRule, ...rules]);
      showToast(editingId ? 'Nationality block updated' : 'Nationality block added', 'success');
      resetForm();
      return;
    }
    const trimmedValue = purposeValue.trim();
    if (!trimmedValue) {
      setFormError('Blocked Word or Code is required.');
      return;
    }
    const duplicate = purposeRules.some(rule => rule.id !== editingId && rule.value.toLowerCase() === trimmedValue.toLowerCase() && rule.type === purposeType && rule.matchType === matchType && rule.partnerScope === normalizeScope(selectedPartner));
    if (duplicate) {
      setFormError('A duplicate value/type/match rule already exists for this partner scope.');
      return;
    }
    const newRule: PurposeRule = {
      id: editingId || `POP-${Date.now()}`,
      partnerScope: normalizeScope(selectedPartner),
      value: trimmedValue,
      type: purposeType,
      matchType,
      effectiveDate,
      endDate
    };
    setPurposeRules(rules => editingId ? rules.map(rule => rule.id === editingId ? newRule : rule) : [newRule, ...rules]);
    showToast(editingId ? 'Purpose of payment block updated' : 'Purpose of payment block added', 'success');
    resetForm();
  };
  const handleEdit = (row: NationalityRule | PurposeRule) => {
    setEditingId(row.id);
    setSelectedPartner(scopeLabel(row.partnerScope));
    setEffectiveDate(row.effectiveDate);
    setEndDate(row.endDate);
    setFormError('');
    if ('nationality' in row) {
      setSelectedNationality(nationalityOptions.find(option => option.code === row.nationalityCode) || null);
    } else {
      setPurposeValue(row.value);
      setPurposeType(row.type);
      setMatchType(row.matchType);
    }
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const confirmDelete = async () => {
    if (!deleteRow) return;
    if (kind === 'nationality') {
      setNationalityRules(rules => rules.filter(rule => rule.id !== deleteRow.id));
    } else {
      setPurposeRules(rules => rules.filter(rule => rule.id !== deleteRow.id));
    }
    setDeleteRow(null);
    showToast('Block rule deleted', 'success');
  };
  const handleExport = () => {
    if (kind === 'nationality') {
      downloadCsv(`${copy.exportPrefix}-${todayStamp()}.csv`, ['Flag/Code', 'Nationality', 'Nationality Code', 'Nationality Code (ISO 8583)', 'Effective Date', 'End Date', 'Partner Scope'], visibleNationalityRules.map(rule => [rule.flag, rule.nationality, rule.nationalityCode, rule.iso8583Code, rule.effectiveDate, rule.endDate || 'Permanent', scopeLabel(rule.partnerScope)]));
    } else {
      downloadCsv(`${copy.exportPrefix}-${todayStamp()}.csv`, ['Blocked Word or Code', 'Type', 'Match Type', 'Effective Date', 'End Date', 'Partner Scope'], visiblePurposeRules.map(rule => [rule.value, rule.type, rule.matchType, rule.effectiveDate, rule.endDate || 'Permanent', scopeLabel(rule.partnerScope)]));
    }
    showToast('Filtered data exported', 'success');
  };
  const resetFilters = () => {
    setFilters({});
  };
  const openActions = (event: React.MouseEvent<HTMLElement>, row: NationalityRule | PurposeRule) => {
    setActionAnchor(event.currentTarget);
    setActionRow(row);
  };
  const closeActions = () => {
    setActionAnchor(null);
    setActionRow(null);
  };
  const handleAction = (action: 'view' | 'edit' | 'delete') => {
    if (!actionRow) return;
    if (action === 'view') setViewRow(actionRow);
    if (action === 'edit') handleEdit(actionRow);
    if (action === 'delete') setDeleteRow(actionRow);
    closeActions();
  };
  const currentRows = kind === 'nationality' ? visibleNationalityRules : visiblePurposeRules;
  const totalRows = currentRows.length;
  const paginatedRows = currentRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const viewSections: EntitySection[] = viewRow ? [{
    title: 'Rule Details',
    fields: 'nationality' in viewRow ? [{
      label: 'Nationality',
      value: viewRow.nationality
    }, {
      label: 'Nationality Code',
      value: viewRow.nationalityCode
    }, {
      label: 'Nationality Code (ISO 8583)',
      value: viewRow.iso8583Code
    }, {
      label: 'Flag/Code Badge',
      value: viewRow.flag
    }] : [{
      label: 'Blocked Word or Code',
      value: viewRow.value
    }, {
      label: 'Type',
      value: viewRow.type
    }, {
      label: 'Match Type',
      value: viewRow.matchType
    }]
  }, {
    title: 'Scope and Dates',
    fields: [{
      label: 'Partner Scope',
      value: scopeLabel(viewRow.partnerScope),
      type: 'chip',
      chipColor: viewRow.partnerScope === 'global' ? '#106898' : '#8A6500',
      chipBgColor: viewRow.partnerScope === 'global' ? 'rgba(16,104,152,0.12)' : 'rgba(240,192,24,0.18)'
    }, {
      label: 'Effective Date',
      value: viewRow.effectiveDate,
      type: 'date'
    }, {
      label: 'End Date',
      value: viewRow.endDate || 'Permanent'
    }]
  }] : [];
  return <Box className="paysys-showcase openremit-rule-designer" sx={{
    display: 'flex',
    minHeight: '100vh',
    bgcolor: styles.pageBg
  }} data-id="element-2969" data-mp="3mmcgexzerkjn">
      <TopBar onMenuClick={() => setMobileOpen(value => !value)} onLogout={onLogout} data-id="element-2970" data-mp="37u0vm4r5haks" />
      <Sidebar open={mobileOpen} onClose={() => setMobileOpen(false)} activePage={copy.activePage} data-id="element-2971" data-mp="3csvq43yhcnkq" />

      <Box component="main" className="paysys-main" sx={{
      flexGrow: 1,
      p: {
        xs: 2,
        md: 3
      },
      width: {
        sm: `calc(100% - ${drawerWidth}px)`
      },
      mt: 8
    }} data-id="element-2972" data-mp="2yxji4uvbd0lv">
        <Container maxWidth="xl" data-id="element-2973" data-mp="19f3f489siscn">
          <Box sx={{
          display: 'grid',
          gap: 1.5
        }} data-id="element-2974" data-mp="19fcy3bwf3z8u">
            <Card className="paysys-card openremit-rule-form-card" elevation={0} sx={{
            borderRadius: 1,
            border: `1px solid ${styles.cardBorder}`,
            boxShadow: styles.cardShadow
          }} data-id="element-2975" data-mp="14n38xy34n37m">
              <CardContent sx={{
              p: {
                xs: 1.75,
                md: 2
              }
            }} data-id="element-2976" data-mp="vmwyym6n0ld7">
                <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 2,
                pb: 1.5,
                mb: 1.5,
                borderBottom: `1px solid ${styles.cardBorder}`,
                flexWrap: 'wrap'
              }} data-id="element-2977" data-mp="voht4ap1i2aj">
                  <Box data-id="element-2978" data-mp="1nem9ly9oi56h">
                    <Typography variant="subtitle1" component="h1" sx={{
                    fontWeight: 800,
                    m: 0,
                    color: '#0057C2'
                  }} data-id="element-2979" data-mp="1jc7sbilnrdhb">{copy.title}</Typography>
                    <Typography variant="caption" color="text.secondary" data-id="element-2980" data-mp="1ec3anp90kcpi">{copy.purpose}</Typography>
                  </Box>
                  <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap'
                }} data-id="element-2981" data-mp="7yni7yoa6nsx">
                    {editingId && <Button variant="outlined" size="small" onClick={resetForm} startIcon={<Icon icon="lucide:x" width={16} data-id="element-2983" data-mp="2emmh5pw702g5" />} data-id="element-2982" data-mp="2wogsuc3q0ym">Cancel Edit</Button>}
                  </Box>
                </Box>
                {formError && <Alert severity="error" sx={{
                mb: 2,
                borderRadius: 1.5
              }} data-id="element-2984" data-mp="2jibm68qzebju">{formError}</Alert>}

                <Box sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: kind === 'nationality' ? 'minmax(260px, 1fr) minmax(220px, 1fr) minmax(220px, 1fr) 74px' : 'minmax(220px, 1fr) minmax(150px, 0.75fr) minmax(170px, 0.8fr) minmax(170px, 0.8fr) minmax(170px, 0.8fr) 74px'
                },
                gap: 1,
                alignItems: 'flex-start'
              }} data-id="element-2985" data-mp="2njws1a53lpkj">
                  {kind === 'nationality' ? <Box data-id="element-2986" data-mp="1cx0w5o81frpt">
                      <Autocomplete options={nationalityOptions} value={selectedNationality} onChange={(_, selected) => setSelectedNationality(selected)} getOptionLabel={option => `${option.name} (${option.iso8583})`} renderInput={params => <TextField {...params} size="small" label="Nationality *" sx={fieldSx} data-id="element-2988" data-mp="16te2njscrhcs" />} data-id="element-2987" data-mp="1cxsevygjduyo" />
                    </Box> : <>
                      <Box data-id="element-2989" data-mp="f26liwkl9xnq">
                        <TextField size="small" label="Blocked Word or Code" value={purposeValue} onChange={event => setPurposeValue(event.target.value)} fullWidth sx={fieldSx} data-id="element-2990" data-mp="f3howva4h3eb" />
                      </Box>
                      <Box data-id="element-2991" data-mp="o3xss5us2cjp">
                        <ToggleButtonGroup value={purposeType} exclusive fullWidth onChange={(_, value: PurposeType | null) => value && handlePurposeTypeChange(value)} sx={{
                      '& .MuiToggleButton-root': {
                        height: 40,
                        fontWeight: 750,
                        py: 0.75
                      }
                    }} data-id="element-2992" data-mp="o11s49rjp9rk">
                          <ToggleButton value="Word" data-id="element-2993" data-mp="1uk02ins7dvbp">Word</ToggleButton>
                          <ToggleButton value="Purpose Code" data-id="element-2994" data-mp="2308u6gokl985">Purpose Code</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                      <Box data-id="element-2995" data-mp="273dse71maoqz">
                        <ToggleButtonGroup value={matchType} exclusive fullWidth onChange={(_, value: MatchType | null) => value && setMatchType(value)} sx={{
                      '& .MuiToggleButton-root': {
                        height: 40,
                        fontWeight: 750,
                        py: 0.75
                      }
                    }} data-id="element-2996" data-mp="26zhmri7vj124">
                          <ToggleButton value="Contains" data-id="element-2997" data-mp="2lq728wsdb2bs">Contains</ToggleButton>
                          <ToggleButton value="Exact Match" data-id="element-2998" data-mp="2qptlbmuvncsi">Exact</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                    </>}
                  <Box data-id="element-2999" data-mp="2h00d8vty1ft0">
                    <TextField size="small" label="Effective Date *" type="date" value={effectiveDate} onChange={event => setEffectiveDate(event.target.value)} fullWidth sx={fieldSx} InputLabelProps={{
                    shrink: true
                  }} data-id="element-3000" data-mp="2gzgrt55349a8" />
                  </Box>
                  <Box data-id="element-3001" data-mp="39hm5crryxno3">
                    <TextField size="small" label="End Date" type="date" value={endDate} onChange={event => setEndDate(event.target.value)} helperText="Leave empty for a permanent block" fullWidth sx={fieldSx} InputLabelProps={{
                    shrink: true
                  }} data-id="element-3002" data-mp="39i488794z04z" />
                  </Box>
                  <Box data-id="element-3003" data-mp="v5y6iaar4ud5">
                    <Tooltip title={editingId ? 'Save rule changes' : 'Add rule'} arrow data-id="element-3004" data-mp="v4dk57ylchqd">
                      <Button fullWidth variant="contained" onClick={handleAddOrSave} sx={{
                      height: 40,
                      fontWeight: 800
                    }} data-id="element-3005" data-mp="1036sklz0tijo">
                        {editingId ? 'Save' : 'Add'}
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Box className="openremit-rule-table" data-id="element-3006" data-mp="h65gyiayok6r">
              <Card elevation={0} data-id="element-3007" data-mp="h6p207vwrop0">
                <CardContent sx={{
                p: 0
              }} data-id="element-3008" data-mp="26ldc22h10ujv">
                  <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 2,
                  flexWrap: 'wrap'
                }} data-id="element-3009" data-mp="26mohs7u18cdm">
                    <Typography variant="caption" sx={{
                    fontWeight: 800,
                    color: styles.muted
                  }} data-id="element-3010" data-mp="2bi3u2vce607x">
                      Showing {totalRows === 0 ? 0 : page * rowsPerPage + 1} of {totalRows} {copy.tableTitle}
                    </Typography>
                    <Box sx={{
                    display: 'flex',
                    gap: 1
                  }} data-id="element-3011" data-mp="2ugyiz9jt5vme">
                      <Button className="openremit-table-button" variant="outlined" size="small" onClick={handleExport} startIcon={<Icon icon="lucide:download" width={14} data-id="element-3013" data-mp="34ew61tpxlmdf" />} data-id="element-3012" data-mp="2uijhfa98typj">
                        Export
                      </Button>
                      <Button className="openremit-table-button reset" variant="contained" size="small" onClick={resetFilters} startIcon={<Icon icon="lucide:refresh-cw" width={14} data-id="element-3015" data-mp="3de9j0wd9kt2w" />} data-id="element-3014" data-mp="2zgkw7izmzbv7">
                        Reset Filters
                      </Button>
                    </Box>
                  </Box>
                  <Divider data-id="element-3016" data-mp="1j0rup751bs6t" />
                  <TableContainer data-id="element-3017" data-mp="1j21r7rw8osbc">
                    <Table data-id="element-3018" data-mp="1ezy5mafuw7cc">
                      <TableHead data-id="element-3019" data-mp="1f0q2dol6wb47">
                        {kind === 'nationality' ? <>
                            <TableRow data-id="element-3020" data-mp="3e5zsx98j2se6">
                              <TableCell data-id="element-3021" data-mp="3e72ldlli12w7" />
                              <TableCell data-id="element-3022" data-mp="3j4ubca2l2exr">Nationality</TableCell>
                              <TableCell data-id="element-3023" data-mp="3o2vvkybqwkat">Nationality Code</TableCell>
                              <TableCell data-id="element-3024" data-mp="3o1krexee1aj6">Nationality Code (ISO 8583)</TableCell>
                              <TableCell data-id="element-3025" data-mp="3t2qjqu44yvs6">Effective Date</TableCell>
                              <TableCell data-id="element-3026" data-mp="yps2c0b10be">End Date</TableCell>
                              <TableCell align="center" data-id="element-3027" data-mp="w3iy7xrmcr2">Actions</TableCell>
                            </TableRow>
                            <TableRow className="openremit-filter-row" data-id="element-3028" data-mp="anzilnfi2axe">
                              <TableCell data-id="element-3029" data-mp="aordsx56q6n5">
                                <FormControl size="small" fullWidth data-id="element-3030" data-mp="fmsujbf51tvg">
                                  <Select value={filters.any || 'all'} onChange={event => setFilter('any', event.target.value === 'all' ? '' : event.target.value)} displayEmpty data-id="element-3031" data-mp="fp5flxg70kcq">
                                    <MenuItem value="all" data-id="element-3032" data-mp="oo0jh2vrlicv">All</MenuItem>
                                    {nationalityOptions.map(option => <MenuItem key={option.iso8583} value={option.iso8583} data-id="element-3033" data-mp="293ououqgiqvt">{option.iso8583}</MenuItem>)}
                                  </Select>
                                </FormControl>
                              </TableCell>
                              <TableCell data-id="element-3034" data-mp="1v6jvnep5usa2">
                                <FilterTextField value={filters.nationality || ''} placeholder="All" onChange={value => setFilter('nationality', value)} data-id="element-3035" data-mp="1v7uwp6r81lxh" />
                              </TableCell>
                              <TableCell data-id="element-3036" data-mp="2nvhaf7u5y6y2">
                                <FilterTextField value={filters.nationalityCode || ''} placeholder="All" onChange={value => setFilter('nationalityCode', value)} data-id="element-3037" data-mp="2j3pjuxb0c17x" />
                              </TableCell>
                              <TableCell data-id="element-3038" data-mp="3bks465d3j12p">
                                <FilterTextField value={filters.iso8583Code || ''} placeholder="All" onChange={value => setFilter('iso8583Code', value)} data-id="element-3039" data-mp="3blusc5j1apex" />
                              </TableCell>
                              <TableCell data-id="element-3040" data-mp="2r9z2bk8b70pr">
                                <FilterTextField type="date" value={filters.effectiveDate || ''} placeholder="" onChange={value => setFilter('effectiveDate', value)} data-id="element-3041" data-mp="2vd5ch2yjlc1i" />
                              </TableCell>
                              <TableCell data-id="element-3042" data-mp="2mbxhouv8itc6">
                                <FilterTextField type="date" value={filters.endDate || ''} placeholder="" onChange={value => setFilter('endDate', value)} data-id="element-3043" data-mp="2mcz4fwlm549q" />
                              </TableCell>
                              <TableCell data-id="element-3044" data-mp="3f0l9zaeeiiz8" />
                            </TableRow>
                          </> : <>
                            <TableRow data-id="element-3045" data-mp="35ajj6aunymgm">
                              <TableCell data-id="element-3046" data-mp="359gwyg8ad871">Blocked Word or Code</TableCell>
                              <TableCell data-id="element-3047" data-mp="3t043oy6gtrs0">Type</TableCell>
                              <TableCell data-id="element-3048" data-mp="1m874u1kdtpa">Match Type</TableCell>
                              <TableCell data-id="element-3049" data-mp="1nj7ejvswjbn">Effective Date</TableCell>
                              <TableCell data-id="element-3050" data-mp="1mblmfp4zrmq8">End Date</TableCell>
                              <TableCell align="center" data-id="element-3051" data-mp="1hdk5pav1fzhx">Actions</TableCell>
                            </TableRow>
                            <TableRow className="openremit-filter-row" data-id="element-3052" data-mp="1v4p8gltpd7wh">
                              <TableCell data-id="element-3053" data-mp="1v6rwx1f7isz3">
                                <FilterTextField value={filters.search || ''} placeholder="Search word/code" onChange={value => setFilter('search', value)} data-id="element-3054" data-mp="1r33pciqkrxdu" />
                              </TableCell>
                              <TableCell data-id="element-3055" data-mp="yfg9ti45vwy2">
                                <FormControl size="small" fullWidth data-id="element-3056" data-mp="ygrbnce1qumj">
                                  <Select value={filters.type || 'all'} onChange={event => setFilter('type', event.target.value)} data-id="element-3057" data-mp="1cf8vgz0tzmz6">
                                    <MenuItem value="all" data-id="element-3058" data-mp="17hh549fu48x3">All</MenuItem>
                                    <MenuItem value="Word" data-id="element-3059" data-mp="2ns1jpzuextah">Word</MenuItem>
                                    <MenuItem value="Purpose Code" data-id="element-3060" data-mp="2ivb2vaasato0">Purpose Code</MenuItem>
                                  </Select>
                                </FormControl>
                              </TableCell>
                              <TableCell data-id="element-3061" data-mp="1levj72argo86">
                                <FormControl size="small" fullWidth data-id="element-3062" data-mp="1li17ifbkfm2u">
                                  <Select value={filters.matchType || 'all'} onChange={event => setFilter('matchType', event.target.value)} data-id="element-3063" data-mp="1pibiy7i4n4p3">
                                    <MenuItem value="all" data-id="element-3064" data-mp="xqvx4foe9lzx">All</MenuItem>
                                    <MenuItem value="Contains" data-id="element-3065" data-mp="xoj6le4km3b9">Contains</MenuItem>
                                    <MenuItem value="Exact Match" data-id="element-3066" data-mp="11sh3rjac541o">Exact Match</MenuItem>
                                  </Select>
                                </FormControl>
                              </TableCell>
                              <TableCell data-id="element-3067" data-mp="1bk3ny54nidgk">
                                <FilterTextField type="date" value={filters.effectiveDate || ''} placeholder="" onChange={value => setFilter('effectiveDate', value)} data-id="element-3068" data-mp="e3eogi36bnac" />
                              </TableCell>
                              <TableCell data-id="element-3069" data-mp="bh5kcxjp0nbc">
                                <FilterTextField type="date" value={filters.endDate || ''} placeholder="" onChange={value => setFilter('endDate', value)} data-id="element-3070" data-mp="bg46ymf7foqg" />
                              </TableCell>
                              <TableCell data-id="element-3071" data-mp="1m3vm2tbxn9a" />
                            </TableRow>
                          </>}
                      </TableHead>
                      <TableBody data-id="element-3072" data-mp="3opieg9cpotx7">
                        {paginatedRows.map(row => <TableRow key={row.id} data-id="element-3073" data-mp="3jwz0hba43wrv">
                            {kind === 'nationality' && 'nationality' in row ? <>
                                <TableCell data-id="element-3074" data-mp="3a045v3gymgiv"><Chip label={row.iso8583Code} size="small" color="primary" variant="outlined" data-id="element-3075" data-mp="3a0cgsptivbrf" /></TableCell>
                                <TableCell data-id="element-3076" data-mp="35wxq957yjczq">{row.nationality}</TableCell>
                                <TableCell data-id="element-3077" data-mp="30ymhkxtef8j3">{row.nationalityCode}</TableCell>
                                <TableCell data-id="element-3078" data-mp="sqg9pndophxw">{row.iso8583Code}</TableCell>
                                <TableCell data-id="element-3079" data-mp="sr96pwmuct2u">{row.effectiveDate}</TableCell>
                                <TableCell data-id="element-3080" data-mp="xo818gaf321k">{row.endDate}</TableCell>
                              </> : 'value' in row ? <>
                                <TableCell data-id="element-3081" data-mp="nqlj7kxtmudd">{row.value}</TableCell>
                                <TableCell data-id="element-3082" data-mp="9zym1x7plcje">{row.type}</TableCell>
                                <TableCell data-id="element-3083" data-mp="9ynh3u2j1yqp">{row.matchType}</TableCell>
                                <TableCell data-id="element-3084" data-mp="erqd08us1ka2">{row.effectiveDate}</TableCell>
                                <TableCell data-id="element-3085" data-mp="22c38qicnmv">{row.endDate}</TableCell>
                              </> : null}
                            <TableCell align="center" data-id="element-3086" data-mp="51na9gxusryj">
                              <IconButton size="small" onClick={event => openActions(event, row)} data-id="element-3087" data-mp="3n6qjnduurq4r">
                                <Icon icon="lucide:more-horizontal" width={18} data-id="element-3088" data-mp="3s5bm7jxkulwd" />
                              </IconButton>
                            </TableCell>
                          </TableRow>)}
                        {paginatedRows.length === 0 && <TableRow data-id="element-3089" data-mp="33n1g16nbak9w">
                            <TableCell colSpan={kind === 'nationality' ? 7 : 6} align="center" sx={{
                          py: 3
                        }} data-id="element-3090" data-mp="3hkpyk7twpfbs">
                              {copy.emptyState}
                            </TableCell>
                          </TableRow>}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Divider data-id="element-3091" data-mp="3mhq6ype8au62" />
                  <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2
                }} data-id="element-3092" data-mp="53kqhhx558jw">
                    <Typography variant="caption" sx={{
                    color: styles.muted,
                    fontWeight: 700
                  }} data-id="element-3093" data-mp="3q1jjwxet1h">Page {page + 1} of {Math.max(1, Math.ceil(totalRows / rowsPerPage))}</Typography>
                    <TablePagination component="div" count={totalRows} page={page} onPageChange={(_, nextPage) => setPage(nextPage)} rowsPerPage={rowsPerPage} onRowsPerPageChange={event => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }} rowsPerPageOptions={[10, 25, 50]} labelRowsPerPage="Items" data-id="element-3094" data-mp="94xx3u825frv" />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Container>
      </Box>

      {viewRow && <EntityViewDrawer open={Boolean(viewRow)} onClose={() => setViewRow(null)} onEdit={() => {
      handleEdit(viewRow);
      setViewRow(null);
    }} title={'nationality' in viewRow ? viewRow.nationality : viewRow.value} subtitle={copy.title} entityId={viewRow.id} sections={viewSections} data-id="element-3095" data-mp="isyom1xrj5rh" />}

      {deleteRow && <ConfirmDialog open={Boolean(deleteRow)} title="Delete Block Rule" message="Are you sure you want to delete this block rule? This will remove it from the configured demo rules." confirmLabel="Delete" onCancel={() => setDeleteRow(null)} onConfirm={confirmDelete} data-id="element-3096" data-mp="2ghu1qhf4hqwi" />}
      <Menu anchorEl={actionAnchor} open={Boolean(actionAnchor)} onClose={closeActions} data-id="element-3097" data-mp="1wsjln2bgt3xv">
        <MenuItem onClick={() => handleAction('view')} data-id="element-3098" data-mp="21qbogrdneagu">
          <Icon icon="lucide:eye" width={16} style={{
          marginRight: 8
        }} data-id="element-3099" data-mp="21r4qjf1nwbsn" /> View
        </MenuItem>
        <MenuItem onClick={() => handleAction('edit')} data-id="element-3100" data-mp="26omz34brop04">
          <Icon icon="lucide:edit" width={16} style={{
          marginRight: 8
        }} data-id="element-3101" data-mp="2bgxx21my60q1" /> Edit
        </MenuItem>
        <MenuItem onClick={() => handleAction('delete')} sx={{
        color: 'error.main'
      }} data-id="element-3102" data-mp="1e226k0jk26uz">
          <Icon icon="lucide:trash-2" width={16} style={{
          marginRight: 8
        }} data-id="element-3103" data-mp="1iztxoscdloy4" /> Delete
        </MenuItem>
      </Menu>
    </Box>;
};
export default BlockRulesPage;